import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { useStore } from '../../context/StoreContext';
import { FiSave, FiImage } from 'react-icons/fi';

const AdminLanding = () => {
  const { fetchLandingSettings } = useStore();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const { data } = await supabase.from('landing_settings').select('*').order('setting_key');
    if (data) setSettings(data);
    setLoading(false);
  };

  const handleChange = (id, field, value) => {
    setSettings(settings.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleSave = async (setting) => {
    setSaving(setting.id);
    await supabase.from('landing_settings').update({
      image_url: setting.image_url,
      bg_color: setting.bg_color,
      text_color: setting.text_color
    }).eq('id', setting.id);
    
    // Refresh the global store so the site updates immediately
    await fetchLandingSettings(); 
    setSaving(null);
  };

  // Group settings by section
  const grouped = settings.reduce((acc, curr) => {
    if (!acc[curr.section]) acc[curr.section] = [];
    acc[curr.section].push(curr);
    return acc;
  }, {});

  if (loading) return <div className="p-8 text-center text-gray-500">Loading configurations...</div>;

  return (
    <div className="space-y-8 pb-12">
      {Object.entries(grouped).map(([section, items]) => (
        <div key={section} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
            <FiImage className="text-brand-gold w-6 h-6" />
            <h2 className="text-xl font-serif text-gray-800">{section} Section Images</h2>
          </div>
          
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {items.map(item => (
              <div key={item.id} className="bg-gray-50/50 p-5 md:p-6 rounded-xl border border-gray-100 flex flex-col gap-5 transition-all hover:shadow-md">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-gray-800 tracking-wide">{item.title}</h3>
                </div>
                
                <div className="aspect-video w-full bg-white rounded-lg overflow-hidden relative border border-gray-200 flex items-center justify-center p-2 shadow-inner">
                  <img src={item.image_url} alt="preview" className="w-full h-full object-contain" />
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Image URL</label>
                  <input 
                    type="text" 
                    value={item.image_url || ''} 
                    onChange={(e) => handleChange(item.id, 'image_url', e.target.value)} 
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all" 
                    placeholder="Enter image URL..."
                  />
                </div>

                {section === 'Hero' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Background</label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color" 
                          value={item.bg_color || '#ffffff'} 
                          onChange={(e) => handleChange(item.id, 'bg_color', e.target.value)} 
                          className="h-10 w-10 rounded cursor-pointer border-0 p-0" 
                        />
                        <input 
                          type="text" 
                          value={item.bg_color || ''} 
                          onChange={(e) => handleChange(item.id, 'bg_color', e.target.value)} 
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase outline-none focus:border-brand-gold" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Text Color</label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color" 
                          value={item.text_color || '#000000'} 
                          onChange={(e) => handleChange(item.id, 'text_color', e.target.value)} 
                          className="h-10 w-10 rounded cursor-pointer border-0 p-0" 
                        />
                        <input 
                          type="text" 
                          value={item.text_color || ''} 
                          onChange={(e) => handleChange(item.id, 'text_color', e.target.value)} 
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase outline-none focus:border-brand-gold" 
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <button 
                  onClick={() => handleSave(item)} 
                  disabled={saving === item.id} 
                  className="mt-2 w-full flex items-center justify-center gap-2 bg-brand-darkSage text-white py-3 rounded-xl font-bold tracking-widest uppercase text-xs hover:bg-brand-gold transition-colors disabled:opacity-50"
                >
                  <FiSave className="w-4 h-4" /> {saving === item.id ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default AdminLanding;